/**
 * Health Connect (`D281`), through the shell's own `HealthConnect` plugin
 * (`android/app/src/main/java/app/mypocketgym/dev/HealthConnectPlugin.kt`):
 * a finished workout becomes one exercise session there. Nothing is read.
 *
 * Three ways to be unavailable, all answered `unavailable` and all silent:
 * the web, a shell older than the plugin (`D166`'s reverse case), and a
 * phone without Health Connect (or below Android 8). `update-required` is
 * Health Connect installed but too old to talk to.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import { isNative } from "./platform";

export type HealthConnectStatus =
  | "available"
  | "unavailable"
  | "update-required";

export interface HealthConnectState {
  /** Whether this app may write exercise sessions. */
  readonly granted: boolean;
  readonly status: HealthConnectStatus;
}

export interface HealthWorkout {
  /** Epoch ms. */
  readonly endedAt: number;
  /** The workout's own id: writing it again updates the same record. */
  readonly id: string;
  /** Epoch ms. */
  readonly startedAt: number;
  readonly title?: string;
}

interface HealthConnectPlugin {
  check(): Promise<{ granted: boolean; status: string }>;
  request(): Promise<{ granted: boolean; status: string }>;
  writeWorkout(workout: HealthWorkout): Promise<void>;
}

const PLUGIN = "HealthConnect";

const UNAVAILABLE: HealthConnectState = {
  granted: false,
  status: "unavailable",
};

let plugin: HealthConnectPlugin | null = null;

function healthConnect(): HealthConnectPlugin {
  plugin ??= registerPlugin<HealthConnectPlugin>(PLUGIN);
  return plugin;
}

/** Whether this shell carries the plugin — false on the web and in older shells. */
export function healthConnectInShell(): boolean {
  return isNative() && Capacitor.isPluginAvailable(PLUGIN);
}

const hasPlugin = healthConnectInShell;

/** Narrows the plugin's answer; anything unknown is unavailable. */
export function healthStateOf(answer: {
  granted?: unknown;
  status?: unknown;
}): HealthConnectState {
  const status =
    answer.status === "available" || answer.status === "update-required"
      ? answer.status
      : "unavailable";
  return { granted: status === "available" && answer.granted === true, status };
}

/** Where Health Connect stands, without asking the person anything. */
export async function healthConnectState(): Promise<HealthConnectState> {
  if (!hasPlugin()) {
    return UNAVAILABLE;
  }
  try {
    return healthStateOf(await healthConnect().check());
  } catch {
    return UNAVAILABLE;
  }
}

/** Opens Health Connect's permission sheet when the grant is missing. */
export async function requestHealthConnect(): Promise<HealthConnectState> {
  if (!hasPlugin()) {
    return UNAVAILABLE;
  }
  try {
    return healthStateOf(await healthConnect().request());
  } catch {
    return UNAVAILABLE;
  }
}

/** Writes one finished workout. Resolves `false` when it was not written. */
export async function writeHealthWorkout(
  workout: HealthWorkout
): Promise<boolean> {
  if (!hasPlugin()) {
    return false;
  }
  try {
    await healthConnect().writeWorkout(workout);
    return true;
  } catch (error) {
    console.warn("[native] Health Connect write failed", error);
    return false;
  }
}
